<template>
  <div class="water-quality-qualification">
    <div class="quality-container">
      <!-- 顶部指标卡片 -->
      <div class="quality-cards">
        <div class="quality-card" v-for="(item, index) in qualityCards" :key="index">
          <div class="card-icon">
            <i :class="item.icon" :style="{ color: item.color }"></i>
          </div>
          <div class="card-content">
            <div class="card-value" :style="{ color: item.color }">{{ item.value }}%</div>
            <div class="card-label">{{ item.label }}</div>
          </div>
        </div>
      </div>

      <!-- 图表区域 -->
      <div class="chart-section">
        <div class="chart-header">
          <span class="chart-title">{{ $t('水质合格率趋势') }}</span>
          <div class="chart-actions">
            <button 
              v-for="period in timePeriods" 
              :key="period.value"
              :class="['period-btn', { active: currentPeriod === period.value }]"
              @click="changePeriod(period.value)"
            >
              {{ period.label }}
            </button>
          </div>
        </div>
        <div class="chart-container" ref="chartContainer">
          <Echart ref="chart" :options="chartOptions" style="height: 100%; width: 100%" />
        </div>
      </div>

      <!-- 底部水质等级分布 -->
      <div class="quality-distribution">
        <div class="distribution-title">{{ $t('水质等级分布') }}</div>
        <div class="distribution-bars">
          <div class="distribution-bar" v-for="(item, index) in distributionData" :key="index">
            <div class="bar-label">{{ item.grade }}</div>
            <div class="bar-container">
              <div 
                class="bar-fill" 
                :style="{ 
                  width: item.percentage + '%', 
                  backgroundColor: item.color 
                }"
              ></div>
            </div>
            <div class="bar-value">{{ item.percentage }}%</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { postAction } from '@/api/manage'

export default {
  name: 'WaterQualityQualificationRate',
  props: {
    onRefresh: {
      type: Function,
      default: () => {}
    }
  },
  data() {
    return {
      loading: false,
      currentPeriod: 'week',
      timePeriods: [
        { label: this.$t('今日'), value: 'today' },
        { label: this.$t('本周'), value: 'week' },
        { label: this.$t('本月'), value: 'month' },
        { label: this.$t('全年'), value: 'year' }
      ],
      qualityCards: [
        { label: this.$t('综合合格率'), value: 98.5, icon: 'fas fa-tint', color: '#00d4ff' },
        { label: this.$t('PH合格率'), value: 99.2, icon: 'fas fa-flask', color: '#00ff88' },
        { label: this.$t('浊度合格率'), value: 97.8, icon: 'fas fa-eye-dropper', color: '#ff6b6b' },
        { label: this.$t('余氯合格率'), value: 98.9, icon: 'fas fa-shield-alt', color: '#ffd93d' }
      ],
      distributionData: [
        { grade: this.$t('优'), percentage: 85, color: '#00ff88' },
        { grade: this.$t('良'), percentage: 12, color: '#00d4ff' },
        { grade: this.$t('中'), percentage: 3, color: '#ffd93d' },
        { grade: this.$t('差'), percentage: 0, color: '#ff6b6b' }
      ],
      chartData: [],
      url: {
        queryQualified: "/jeecg-charge/wq/index/queryQualified"
      }
    }
  },
  computed: {
    chartOptions() {
      return {
        backgroundColor: 'transparent',
        textStyle: {
          color: '#fff',
          fontSize: 12
        },
        grid: {
          top: '10%',
          left: '3%',
          right: '4%',
          bottom: '8%',
          containLabel: true
        },
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(0, 26, 51, 0.9)',
          borderColor: '#0096ff',
          borderWidth: 1,
          textStyle: {
            color: '#fff'
          },
          formatter: (params) => {
            const param = params[0]
            return `${param.name}<br/>${param.marker}${this.$t('合格率')}: ${param.value}%`
          }
        },
        xAxis: {
          type: 'category',
          data: this.getXAxisData(),
          axisLine: {
            lineStyle: {
              color: 'rgba(255, 255, 255, 0.2)'
            }
          },
          axisLabel: {
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: 11,
            rotate: this.currentPeriod === 'year' ? 45 : 0
          },
          splitLine: {
            show: false
          }
        },
        yAxis: {
          type: 'value',
          min: 90,
          max: 100,
          axisLine: {
            lineStyle: {
              color: 'rgba(255, 255, 255, 0.2)'
            }
          },
          axisLabel: {
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: 11,
            formatter: '{value}%'
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(255, 255, 255, 0.1)',
              type: 'dashed'
            }
          }
        },
        series: [{
          name: this.$t('水质合格率'),
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: {
            color: '#00d4ff',
            width: 3,
            shadowBlur: 10,
            shadowColor: 'rgba(0, 212, 255, 0.5)'
          },
          itemStyle: {
            color: '#00d4ff',
            borderColor: '#fff',
            borderWidth: 2
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(0, 212, 255, 0.3)' },
                { offset: 1, color: 'rgba(0, 212, 255, 0.05)' }
              ]
            }
          },
          data: this.getChartData(),
          markLine: {
            silent: true,
            lineStyle: {
              color: '#ff6b6b',
              type: 'dashed'
            },
            data: [{
              yAxis: 95,
              label: {
                formatter: this.$t('标准线: 95%'),
                color: '#ff6b6b'
              }
            }]
          }
        }]
      }
    }
  },
  mounted() {
    this.loadData()
    this.generateMockData()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    loadData() {
      this.loading = true
      postAction(this.url.queryQualified, { period: this.currentPeriod })
        .then(res => {
          if (res.success) {
            this.chartData = res.result.map(item => ({
              time: item.createTime,
              value: item.passrate * 100
            })) || []
            this.updateQualityCards()
          }
        })
        .catch(err => {
          console.error('水质数据加载失败:', err)
          // 使用模拟数据
          this.generateMockData()
        })
        .finally(() => {
          this.loading = false
        })
    },
    generateMockData() {
      const periods = {
        today: Array.from({ length: 24 }, (_, i) => ({
          time: `${i.toString().padStart(2, '0')}:00`,
          value: 95 + Math.random() * 5
        })),
        week: [this.$t('周一'), this.$t('周二'), this.$t('周三'), this.$t('周四'), this.$t('周五'), this.$t('周六'), this.$t('周日')].map(day => ({
          time: day,
          value: 95 + Math.random() * 5
        })),
        month: Array.from({ length: 30 }, (_, i) => ({
          time: `${i + 1}`,
          value: 95 + Math.random() * 5
        })),
        year: [this.$t('1月'), this.$t('2月'), this.$t('3月'), this.$t('4月'), this.$t('5月'), this.$t('6月'), this.$t('7月'), this.$t('8月'), this.$t('9月'), this.$t('10月'), this.$t('11月'), this.$t('12月')].map(month => ({
          time: month,
          value: 95 + Math.random() * 5
        }))
      }
      this.chartData = periods[this.currentPeriod] || []
    },
    updateQualityCards() {
      // 根据实际数据更新卡片
      if (this.chartData.length > 0) {
        const latest = this.chartData[this.chartData.length - 1]
        this.qualityCards[0].value = latest.value.toFixed(1)
      }
    },
    changePeriod(period) {
      this.currentPeriod = period
      this.loadData()
      this.generateMockData()
    },
    getXAxisData() {
      return this.chartData.map(item => item.time)
    },
    getChartData() {
      return this.chartData.map(item => item.value.toFixed(1))
    },
    handleResize() {
      if (this.$refs.chart && this.$refs.chart.chart) {
        this.$refs.chart.chart.resize()
      }
    }
  }
}
</script>

<style lang="less" scoped>
.water-quality-qualification {
  width: 100%;
  height: 100%;
  padding: 0.5rem;
  overflow: hidden;
}

.quality-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* 顶部指标卡片 */
.quality-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1.5fr));
  gap: 0.5rem;
  flex-shrink: 0;
}

.quality-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 150, 255, 0.2);
  border-radius: 8px;
  padding: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 150, 255, 0.3);
    border-color: rgba(0, 150, 255, 0.5);
  }

  .card-icon {
    font-size: 1.5rem;
    opacity: 0.9;
  }

  .card-content {
    flex: 1;

    .card-value {
      font-size: 1.25rem;
      font-weight: 700;
      font-family: 'ds-digital-bold', monospace;
      text-shadow: 0 0 10px currentColor;
    }

    .card-label {
      font-size: 0.75rem;
      color: rgba(255, 255, 255, 0.7);
      margin-top: 0.125rem;
    }
  }
}

/* 图表区域 */
.chart-section {
  flex: 1;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(0, 150, 255, 0.1);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(0, 150, 255, 0.1);
  flex-shrink: 0;

  .chart-title {
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.9);
    font-weight: 600;
  }

  .chart-actions {
    display: flex;
    gap: 0.25rem;

    .period-btn {
      padding: 0.25rem 0.5rem;
      font-size: 0.75rem;
      border: 1px solid rgba(0, 150, 255, 0.3);
      background: rgba(0, 150, 255, 0.1);
      color: rgba(255, 255, 255, 0.8);
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        background: rgba(0, 150, 255, 0.2);
        border-color: rgba(0, 150, 255, 0.5);
      }

      &.active {
        background: rgba(0, 150, 255, 0.3);
        border-color: rgba(0, 150, 255, 0.6);
        color: #fff;
        box-shadow: 0 0 10px rgba(0, 150, 255, 0.3);
      }
    }
  }
}

.chart-container {
  flex: 1;
  padding: 0.5rem;
}

/* 底部分布 */
.quality-distribution {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(0, 150, 255, 0.1);
  border-radius: 8px;
  padding: 0.75rem;
  flex-shrink: 0;

  .distribution-title {
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 0.5rem;
    font-weight: 600;
  }

  .distribution-bars {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .distribution-bar {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .bar-label {
      font-size: 0.75rem;
      color: rgba(255, 255, 255, 0.8);
      width: 20px;
      text-align: center;
    }

    .bar-container {
      flex: 1;
      height: 8px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 4px;
      overflow: hidden;

      .bar-fill {
        height: 100%;
        transition: width 0.8s ease;
        border-radius: 4px;
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
      }
    }

    .bar-value {
      font-size: 0.75rem;
      color: rgba(255, 255, 255, 0.9);
      font-weight: 600;
      width: 35px;
      text-align: right;
      font-family: 'ds-digital-bold', monospace;
    }
  }
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .quality-cards {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .quality-cards {
    grid-template-columns: 1fr;
  }

  .chart-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .chart-actions {
    align-self: stretch;
    justify-content: space-between;

    .period-btn {
      flex: 1;
      text-align: center;
    }
  }
}

@media (max-width: 480px) {
  .water-quality-qualification {
    padding: 0.25rem;
  }

  .quality-container {
    gap: 0.25rem;
  }

  .quality-card {
    padding: 0.5rem;
  }

  .distribution-bars {
    gap: 0.25rem;
  }
}

/* 加载动画 */
.loading {
  .quality-container {
    opacity: 0.6;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 40px;
    height: 40px;
    margin: -20px 0 0 -20px;
    border: 3px solid rgba(0, 150, 255, 0.3);
    border-top: 3px solid #00d4ff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

/* 数字字体 */
@font-face {
  font-family: 'ds-digital-bold';
  src: url('data:font/woff2;base64,') format('woff2');
  font-display: swap;
}
</style>